import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { FacilityResponse, Schema } from "@bw/shared/types/generated";

export type FacilityReviewDialog = "approve" | "reject" | null;
export type FacilityActionKind = "approve" | "reject" | "assessment";

export type FacilityActionVariables =
  | Readonly<{ kind: "approve"; facilityId: string }>
  | Readonly<{
      kind: "reject";
      facilityId: string;
      payload: Schema["FacilityApprovalRejectRequest"];
    }>
  | Readonly<{
      kind: "assessment";
      facilityId: string;
      payload: Schema["AssessmentCenterStatusUpdate"];
    }>;

export type FacilityActiveAction = Readonly<{
  facilityId: string;
  kind: FacilityActionKind;
}>;

export type FacilityActionError = Readonly<{
  facilityId: string;
  kind: FacilityActionKind;
  message: string;
}>;

export type RunFacilityAction = (
  variables: FacilityActionVariables,
  onSuccess?: (facility: FacilityResponse) => void,
) => boolean;

const rejectionSchema = z.object({
  reason: z
    .string()
    .max(1000, "Use 1,000 characters or fewer.")
    .refine((value) => value.trim().length > 0, {
      message: "Enter a rejection reason.",
    }),
}) satisfies z.ZodType<Schema["FacilityApprovalRejectRequest"]>;

type RejectDraft = z.infer<typeof rejectionSchema>;

export function FacilityReviewDialogs({
  approvalEligible,
  dialog,
  error,
  facilityAuthoritative,
  facilityId,
  facilityName,
  facilityRetrying,
  pending,
  readinessAuthoritative,
  readinessRetrying,
  rejectionEligible,
  onAction,
  onClose,
  onRetryApproval,
  onRetryFacility,
}: {
  approvalEligible: boolean;
  dialog: FacilityReviewDialog;
  error: FacilityActionError | null;
  facilityAuthoritative: boolean;
  facilityId: string;
  facilityName: string;
  facilityRetrying: boolean;
  pending: boolean;
  readinessAuthoritative: boolean;
  readinessRetrying: boolean;
  rejectionEligible: boolean;
  onAction: RunFacilityAction;
  onClose: () => void;
  onRetryApproval: () => void;
  onRetryFacility: () => void;
}) {
  const { getValues, register, reset, watch } = useForm<RejectDraft>({
    resolver: zodResolver(rejectionSchema, undefined, { mode: "sync" }),
    mode: "onChange",
    defaultValues: { reason: "" },
  });
  const acceptedSubmissionRef = useRef(false);
  const sawPendingRef = useRef(false);
  const [acceptedSubmission, setAcceptedSubmission] = useState(false);
  const reason = watch("reason");
  const validation = rejectionSchema.safeParse({ reason });
  const helperText = validation.success
    ? undefined
    : validation.error.issues[0]?.message;
  const visibleError = error?.facilityId === facilityId ? error : null;
  const locked = pending || acceptedSubmission;
  const approvalAuthorityMissing =
    !facilityAuthoritative || !readinessAuthoritative;
  const approvalRetrying = facilityRetrying || readinessRetrying;

  useEffect(() => {
    if (pending) {
      sawPendingRef.current = true;
      return;
    }
    if (sawPendingRef.current) {
      sawPendingRef.current = false;
      acceptedSubmissionRef.current = false;
      setAcceptedSubmission(false);
    }
  }, [pending]);

  useEffect(
    () => () => {
      acceptedSubmissionRef.current = false;
    },
    [],
  );

  function dismiss() {
    if (pending || acceptedSubmissionRef.current) return;
    if (dialog === "reject") reset({ reason: "" });
    onClose();
  }

  function approve() {
    if (locked || !approvalEligible) return;
    const accepted = onAction({ kind: "approve", facilityId }, () => onClose());
    if (accepted) {
      acceptedSubmissionRef.current = true;
      setAcceptedSubmission(true);
    }
  }

  function reject() {
    if (locked || !rejectionEligible) return;
    const result = rejectionSchema.safeParse(getValues());
    if (!result.success) return;
    const payload: Schema["FacilityApprovalRejectRequest"] = {
      reason: result.data.reason.trim(),
    };
    const accepted = onAction({ kind: "reject", facilityId, payload }, () => {
      reset({ reason: "" });
      onClose();
    });
    if (accepted) {
      acceptedSubmissionRef.current = true;
      setAcceptedSubmission(true);
    }
  }

  return (
    <>
      {dialog === "approve" && (
        <Dialog
          open
          onClose={dismiss}
          aria-labelledby="facility-approval-dialog-title"
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle id="facility-approval-dialog-title">
            Approve facility
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography component="h3" variant="subtitle2">
                {facilityName}
              </Typography>
              <DialogContentText>
                Confirm that this facility is ready for platform approval.
              </DialogContentText>
              {!approvalEligible && !locked ? (
                <Alert
                  severity="warning"
                  role="alert"
                  action={
                    approvalAuthorityMissing ? (
                      <Button
                        color="inherit"
                        size="small"
                        disabled={approvalRetrying}
                        onClick={onRetryApproval}
                      >
                        Retry
                      </Button>
                    ) : undefined
                  }
                >
                  {!facilityAuthoritative
                    ? "Facility data could not be refreshed or is no longer current. Retry before continuing."
                    : "Approval eligibility changed or is refreshing. Refresh facility data before trying again."}
                </Alert>
              ) : visibleError?.kind === "approve" ? (
                <Alert severity="error" role="alert">
                  {visibleError.message}
                </Alert>
              ) : null}
              {acceptedSubmission && pending && (
                <Box
                  role="status"
                  aria-label="Approving facility"
                  aria-live="polite"
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" disabled={locked} onClick={dismiss}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={locked || !approvalEligible}
              onClick={approve}
            >
              {acceptedSubmission && pending
                ? "Approving…"
                : "Confirm approval"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialog === "reject" && (
        <Dialog
          open
          onClose={dismiss}
          aria-labelledby="facility-rejection-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              reject();
            }}
          >
            <DialogTitle id="facility-rejection-dialog-title">
              Reject facility
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Typography component="h3" variant="subtitle2">
                  {facilityName}
                </Typography>
                <TextField
                  {...register("reason")}
                  autoFocus
                  fullWidth
                  multiline
                  minRows={4}
                  disabled={locked}
                  label="Rejection reason"
                  placeholder="Document what the facility must correct"
                  error={reason.length > 0 && !validation.success}
                  helperText={reason.length > 0 ? helperText : undefined}
                  slotProps={{ htmlInput: { maxLength: 1000 } }}
                />
                {!rejectionEligible && !locked ? (
                  <Alert
                    severity="warning"
                    role="alert"
                    action={
                      !facilityAuthoritative ? (
                        <Button
                          color="inherit"
                          size="small"
                          disabled={facilityRetrying}
                          onClick={onRetryFacility}
                        >
                          Retry
                        </Button>
                      ) : undefined
                    }
                  >
                    {!facilityAuthoritative
                      ? "Facility data could not be refreshed or is no longer current. Retry before continuing."
                      : "Facility review eligibility changed or is refreshing. Refresh facility data before trying again."}
                  </Alert>
                ) : visibleError?.kind === "reject" ? (
                  <Alert severity="error" role="alert">
                    {visibleError.message}
                  </Alert>
                ) : null}
                {acceptedSubmission && pending && (
                  <Box
                    role="status"
                    aria-label="Rejecting facility"
                    aria-live="polite"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button size="small" disabled={locked} onClick={dismiss}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="small"
                color="error"
                variant="contained"
                disabled={locked || !validation.success || !rejectionEligible}
              >
                {acceptedSubmission && pending
                  ? "Rejecting…"
                  : "Confirm rejection"}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </>
  );
}
